import { useUser } from "@auth0/nextjs-auth0";
import Tooltip from "@reach/tooltip";
import React, { useState } from "react";
import styled from "styled-components";

import { Avatar } from "../avatar/Avatar";
import { AvatarPlaceholder } from "../avatar/AvatarPlaceholder";
import DateTimeFormat from "../date-time/DateTime";
import { InlineSeparator } from "../item/InlineSeparator";
import {
  AvatarContainer,
  PosterInformationElement
} from "../item/PosterInformation";
import OptionsMenu from "../options-menu/OptionsMenu";
import Text from "../text/Text";
import TextArea from "../textarea/TextArea";
import VerifiedLabel from "../verified-label/VerifiedLabel";
import type { ItemComment } from "../../data/db/types";
import useDeleteComment from "../../hooks/useDeleteComment";
import useMutateComment from "../../hooks/useMutateComment";
import { trueOrUndef } from "../../utils/dom-attribute-helpers";
import { isMe, SourceUser } from "../../utils/is-me";
import { isBlessedUser } from "../../utils/user-role";

const CommentElement = styled.div<{ disabled?: true | null }>`
  position: relative;
  &[disabled] {
    opacity: 0.5;
  }
`;
const CommentText = styled.div`
  margin-top: var(--spacing-default);
  margin-bottom: var(--spacing-large);
`;
const StyledOptionsMenu = styled(OptionsMenu)`
  margin-left: auto;
`;

type Props = {
  comment: ItemComment;
  onCommentChange?: () => void;
};

const Comment: React.FC<Props> = ({ comment, onCommentChange }) => {
  const [isEditing, setIsEditing] = useState(false);
  const { user } = useUser();
  const { _id, text, author, dateCreated } = comment;
  const [commentText, setCommentText] = useState(text);
  const canEdit = isMe(user as SourceUser, author) || isBlessedUser(user);
  const { value, isSubmitting, ...commentInputProps } = useMutateComment(
    { _id },
    {
      initialValue: text,
      clearOnSubmit: false,
      onSuccess({ comment }) {
        setIsEditing(false);
        setCommentText(comment.text);
        if (onCommentChange) {
          onCommentChange();
        }
      }
    }
  );
  const { deleteComment, isDeleting } = useDeleteComment(_id, {
    onSuccess() {
      if (onCommentChange) {
        onCommentChange();
      }
    }
  });
  return (
    <CommentElement disabled={trueOrUndef(isDeleting)}>
      <PosterInformationElement>
        <AvatarContainer>
          {author.image ? (
            <Avatar level="3" src={author.image} alt={author.name} role="img" />
          ) : (
            <AvatarPlaceholder level="3" role="img" />
          )}
          <Text level="2">
            {author && author._verifiedContributor ? (
              <Tooltip label="A 'Verified Contributor' is an official member of the IOG organisation.">
                <VerifiedLabel role="tooltip">{author.name}</VerifiedLabel>
              </Tooltip>
            ) : (
              author.name
            )}
          </Text>
        </AvatarContainer>
        <InlineSeparator />
        <Text isMuted level="2">
          <DateTimeFormat value={dateCreated} />
        </Text>
        {canEdit && (
          <StyledOptionsMenu
            role="menu"
            name="edit-comment"
            options={[
              {
                onSelect: () => {
                  setIsEditing(!isEditing);
                },
                children: "Edit"
              },
              {
                onSelect: () => {
                  if (confirm("Are you sure you want to delete?")) {
                    deleteComment();
                  }
                },
                children: "Delete"
              }
            ]}
          />
        )}
      </PosterInformationElement>
      <CommentText>
        {isEditing ? (
          <TextArea
            value={value}
            autoFocus
            onBlur={() => {
              if (!isSubmitting) {
                setIsEditing(false);
              }
            }}
            {...commentInputProps}
            disabled={trueOrUndef(isSubmitting)}
          />
        ) : (
          commentText
        )}
      </CommentText>
    </CommentElement>
  );
};

export default Comment;
